import type { LuluConversation, WorkboardSnapshot } from './queries'
import type { ConversationMode } from './backend-contract'
import type { LinkState, SessionState } from './session-state'

/**
 * 把「后端此刻真能给出的东西」收敛成一份界面能直接读的快照。
 *
 * # 为什么要有这一层，而不是在组件里就地取值
 *
 * 因为**这一层是唯一知道「我们其实不知道」的地方**。
 *
 * 设计稿要的 12 态来自 `AgentSession.state`，而那张表没有任何浏览器可达的读接口
 * （见 `backend-contract.ts`）。浏览器真正拿得到的只有 `Conversation.state`
 * 那四档，它说的是另一件事：这条会话本身是开着、等审批、结束了，还是归档了。
 *
 * 把这两件事混成一件，界面就会拿「会话是 active」去显示「团队已开始执行」——
 * 而那句话在一条刚建好、一个 Agent 都没派的会话上就是假的。
 *
 * 所以这里做两件事：能映射的老实映射，映射不到的老实说不知道，
 * 并把「为什么不知道」一起带出去，让界面能显示「这一块还没接上」而不是一片空白。
 */

/** 浏览器真能读到的会话状态。来自 openapi `Conversation.state`。 */
export type WireConversationState = LuluConversation['state']

/**
 * `Conversation.state` → 设计稿 12 态。
 *
 * 只有两档对得上，而且都是对得很实的那种：
 *
 *   waiting_approval → waiting_user   两边说的都是「有事等你点」
 *   completed        → completed      两边说的都是「这件事结束了」
 *
 * `active` **刻意不映射**。它的意思是「这条会话没关闭」，而不是「有人在干活」。
 * 把它映射成 `running` 是这份文件存在的理由：那会让一条刚建好、一个 Agent
 * 都没派的空会话显示成「团队已开始执行」。
 *
 * `archived` 也不映射——归档是会话的属性，不是工作的状态，见下面 `archived` 字段。
 */
const CONVERSATION_TO_SESSION: Partial<Record<WireConversationState, SessionState>> = {
  waiting_approval: 'waiting_user',
  completed: 'completed',
}

export function sessionStateFromConversation(state: WireConversationState): SessionState | null {
  return CONVERSATION_TO_SESSION[state] ?? null
}

/**
 * 执行会话的内核 14 档 → 设计稿 12 态。
 *
 * # 这是第三份映射，而三份都不是重复
 *
 * `itemStateFromWire` 映的是**工作项**的九档，`sessionStateForTeam` 映的是
 * 执行会话的十四档到**工作板行**上的档，这一份映的是同样十四档到**会话级**
 * 的十二档。三套词表只是有几个词长得一样。合成一个函数的那天，后端给其中
 * 一边加一档，另外两边会静默跟着变。
 *
 * # 为什么这一份把 8 档接上了，而 `Conversation.state` 只能接 2 档
 *
 * `Conversation.state` 的枚举只有四档（active/waiting_approval/completed/
 * archived），说的是「这条会话开着没有」。12 态说的是「这一轮活干到哪了」。
 * 从四档里**推不出**十二档——这就是 8 档一直显示不出来的全部原因，
 * 而工作板的 `WorkboardSession.state` 一直带着那十四档在 wire 上。
 *
 * # 刻意不映的那几档
 *
 *   provisioning / ready  会话开好了但还没开工。12 态里最近的是 planning，
 *                         而 planning 说的是「Lulu 在拆任务」——那是另一件事。
 *   reassigned / fenced   这条会话被换掉了。**它不是 completed**：
 *   cancelled             活可能还在别人手上跑着。合成 completed 会让用户
 *                         以为事情结束了（CLAUDE.md §3「两件事如果要求用户
 *                         做的下一个动作不同，就不许合成一种说法」）。
 *
 * 这几档回 null，显示层落到 unknown——「我不知道」是一句真话。
 */
export function sessionStateFromRuntime(state: string): SessionState | null {
  switch (state) {
    case 'running':
      return 'running'
    case 'waiting_agent':
      return 'waiting_agent'
    case 'waiting_user':
      return 'waiting_user'
    case 'blocked':
      return 'blocked'
    case 'disconnected':
      return 'disconnected'
    case 'recovering':
      return 'recovering'
    case 'platform_held':
      return 'platform_held'
    case 'completed':
      return 'completed'
    case 'failed':
      return 'failed'
    default:
      // 认不出的回 null，**绝不兜底成某一档**——兜底会让后端新加的一档
      // 静默显示成一句我们并不知道的话，而没有人会发现它加了。
      return null
  }
}

/**
 * 挑出这条会话当下的主执行会话。
 *
 * **只看 primary。** subagent 是主 Agent 派下去的临时执行单元，
 * 拿它的状态当会话级状态，会让「一个子 Agent 卡住了」显示成
 * 「整条会话卡住了」——而设计稿整节都在讲这两者不是一回事。
 *
 * 有多条 primary 时取 `updated_at` 最新的那条。库里没有唯一索引拦得住
 * 多条 primary（2026-08-28 在真库上查过 pg_indexes），所以这里不能假设只有一条。
 */
export function primarySessionOf(
  workboard: WorkboardSnapshot | null | undefined,
): { readonly state: string } | null {
  const sessions = workboard?.sessions
  if (!Array.isArray(sessions)) return null
  let best: { state: string; updated_at?: string } | null = null
  for (const s of sessions) {
    if (s.session_role !== 'primary') continue
    if (!best || String(s.updated_at ?? '') > String(best.updated_at ?? '')) {
      best = { state: s.state, updated_at: s.updated_at }
    }
  }
  return best
}

export interface LuluSnapshot {
  readonly conversationId: string | null
  readonly title: string
  /** 为空就是通用会话。设计稿：这不是错误状态。 */
  readonly projectId: string | null
  readonly archived: boolean
  /**
   * 单 Agent 还是团队。
   *
   * 后端的 `collaboration_mode` 还没有浏览器可达的读接口，所以今天永远是 null。
   * **null 不等于 team**：界面在拿不到这个值的时候不许渲染团队入口，
   * 因为「单 Agent 会话没有团队入口」是硬规则，猜错的那一侧会凭空多出一个按钮。
   */
  readonly mode: ConversationMode | null
  /** 会话级 12 态。拿不到就是 null，由显示层落到 unknown。 */
  readonly sessionState: SessionState | null
  readonly link: LinkState
}

export interface SnapshotInput {
  readonly conversation: LuluConversation | null
  /**
   * 这条会话的工作板。**12 态里的 8 档只能从这儿来。**
   *
   * 可以缺席（还没取到、或者这条会话压根没有工作板）：缺席时退回
   * `Conversation.state` 能给的那两档，与 2026-08-28 之前一模一样。
   */
  readonly workboard?: WorkboardSnapshot | null
  /** 取数是不是失败了。失败就是 link down——界面据此撤掉所有「进行中」。 */
  readonly transportFailed: boolean
}

export function buildSnapshot({
  conversation,
  workboard,
  transportFailed,
}: SnapshotInput): LuluSnapshot {
  if (!conversation) {
    return {
      conversationId: null,
      title: '',
      projectId: null,
      archived: false,
      mode: null,
      sessionState: null,
      link: transportFailed ? 'down' : 'live',
    }
  }
  return {
    conversationId: conversation.id,
    title: conversation.title,
    // openapi 里这个字段是可选的：没有它就是通用会话。
    projectId: conversation.project_id ?? null,
    archived: conversation.state === 'archived',
    // 接真实字段。openapi：`collaboration_mode: solo|team|null`——
    // **wire 与界面是同一套词**，库里那个 'single' 由服务端在出口翻译，
    // 前端不再有第二份词表（旧的 toWireMode/fromWireMode 已删）。
    //
    // null 是历史会话，**既不是 team 也不是 solo**：界面据此不渲染
    // 团队入口，而不是猜一个。
    mode: conversation.collaboration_mode ?? null,
    // ## 谁说了算
    //
    // 会话级那两档先判（waiting_approval / completed）：它们是**会话本身**
    // 的事实，而且 waiting_approval 那一档是唯一一句「有事等你点」——
    // 让执行会话的状态盖住它，用户会漏掉一个要他动手的时刻。
    //
    // 会话说 active（推不出任何东西）时才看执行会话。这一步把 8 档接上了。
    sessionState:
      sessionStateFromConversation(conversation.state) ??
      (primarySessionOf(workboard)
        ? sessionStateFromRuntime(primarySessionOf(workboard)!.state)
        : null),
    link: transportFailed ? 'down' : 'live',
  }
}
